/**
 * UnifiedGamePage - Single unified game viewing experience.
 * Adapts layout based on game state (NOT_STARTED, LIVE, COMPLETED)
 * and whether StatMoose GameCast data is available.
 */
import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Trophy } from "lucide-react";
import { ASNLoader } from "./ASNLoader";
import { AdUnit } from "./AdUnit";
import { ShareButtons } from "./ShareButtons";
import { FanPollWidget } from "./FanPollWidget";
import { LiveStatsPanel } from "./LiveStatsPanel";
import { FootballStatsPanel } from "./FootballStatsPanel";
import { PlayByPlayWidget } from "./PlayByPlayWidget";
import { GameChat } from "./GameChat";
import { StatMooseGameCast, useGameCastData, GameCastHeader } from "./StatMooseGameCast";
import ScoreBirdGameCast, { useScoreBirdData } from "./ScoreBirdGameCast";
import {
  TeamBannerHeader,
  VideoPlayer,
  GameCountdown,
  GameInfoPanel,
  StatLeaders,
  Next3GamesWidget,
  TabbedContent,
  MaxPrepsTeamInfo,
  extractStatMooseCode,
  API,
} from "./game";
import axios from "axios";

// ============================================
// LAYOUT FRAGMENTS — reduce repetition in state branches
// ============================================
const VideoWithShare = ({ broadcast, isLive, liveScore }) => (
  <>
    <VideoPlayer
      broadcast={broadcast}
      isLive={isLive}
      liveScore={liveScore}
      isExpanded={false}
      onToggleExpand={() => {}}
    />
    <div className="flex items-center justify-end gap-3">
      <ShareButtons title={broadcast.title} url={window.location.href} />
    </div>
  </>
);

// ============================================
// MAIN UNIFIED GAME PAGE
// ============================================
export const UnifiedGamePage = () => {
  const { broadcastId } = useParams();
  const navigate = useNavigate();
  const [broadcast, setBroadcast] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [boxScoreData, setBoxScoreData] = useState(null);
  const [liveScore, setLiveScore] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Fetch all data ──
  useEffect(() => {
    const fetchData = async () => {
      try {
        const broadcastRes = await axios.get(`${API}/broadcasts/schedule/${broadcastId}`);
        const bd = broadcastRes.data;
        setBroadcast(bd);

        if (bd.event_id) {
          try {
            const eventRes = await axios.get(`${API}/events/${bd.event_id}`);
            setEventData(eventRes.data);
          } catch { /* event data optional */ }
        }

        if (bd.stats_link) {
          try {
            const boxRes = await axios.get(`${API}/broadcasts/schedule/${broadcastId}/boxscore`);
            if (boxRes.data?.has_boxscore || boxRes.data?.data?.iframe_url) {
              setBoxScoreData(boxRes.data);
              const d = boxRes.data?.data;
              if (d?.home_score !== undefined || d?.away_score !== undefined) {
                setLiveScore({
                  home_score: d.home_score ?? 0,
                  away_score: d.away_score ?? 0,
                  period: d.period || "",
                  status: d.status || "unknown",
                });
              }
            }
          } catch { /* boxscore optional */ }
        }
      } catch {
        navigate("/schedule");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [broadcastId, navigate]);

  // ── Live score polling ──
  useEffect(() => {
    if (!broadcast?.is_live || !broadcast?.stats_link) return;
    const fetchLiveScore = async () => {
      if (document.hidden) return;
      try {
        const res = await axios.get(`${API}/broadcasts/schedule/${broadcastId}/boxscore`);
        if (res.data?.data) {
          setLiveScore({
            home_score: res.data.data.home_score,
            away_score: res.data.data.away_score,
            period: res.data.data.period,
            status: res.data.data.status,
          });
        }
      } catch { /* live score fetch optional */ }
    };
    fetchLiveScore();
    const interval = setInterval(fetchLiveScore, 10000);
    return () => clearInterval(interval);
  }, [broadcast?.is_live, broadcast?.stats_link, broadcastId]);

  // ── StatMoose hooks (must be before early returns) ──
  const statMooseCode = extractStatMooseCode(broadcast?.stats_link);
  const { data: gameCastData } = useGameCastData(statMooseCode);
  const broadcastOverrides = useMemo(
    () => ({
      home: typeof broadcast?.home_team === "object" ? broadcast.home_team : null,
      away: typeof broadcast?.away_team === "object" ? broadcast.away_team : null,
    }),
    [broadcast?.home_team, broadcast?.away_team]
  );

  // ── ScoreBird hooks ──
  const scorebirdLink = broadcast?.scorebird_link || null;
  const { data: scorebirdData } = useScoreBirdData(scorebirdLink);

  // ── Loading ──
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <ASNLoader />
      </div>
    );
  }

  // ── Not found ──
  if (!broadcast) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Trophy className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h1 className="text-white text-2xl font-bold mb-2">Game Not Found</h1>
          <Link to="/schedule" className="text-orange-500 hover:text-orange-400">
            Back to Schedule
          </Link>
        </div>
      </div>
    );
  }

  const isLive = broadcast.is_live;
  const isCompleted = broadcast.is_completed;
  const isNotStarted = !isLive && !isCompleted;
  const hasGameCast = statMooseCode && gameCastData;
  const hasScoreBird = !!scorebirdLink && !!scorebirdData && !isCompleted;
  const homeTeamObj = typeof broadcast.home_team === "object" ? broadcast.home_team : { name: broadcast.home_team };
  const awayTeamObj = typeof broadcast.away_team === "object" ? broadcast.away_team : { name: broadcast.away_team };

const hasMaxPreps = !!(broadcast?.home_maxpreps_link || broadcast?.away_maxpreps_link);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="unified-game-page">
      {/* Tournament Banner */}
      {eventData?.tournament_banner_url && (
        <div className="bg-zinc-950 py-2">
          <div className="max-w-6xl mx-auto px-4">
            {eventData.tournament_banner_link ? (
              <a href={eventData.tournament_banner_link} target="_blank" rel="noopener noreferrer" className="block mx-auto" data-testid="tournament-banner">
                <img src={eventData.tournament_banner_url} alt={`${eventData.name || "Tournament"} Banner`} className="w-full h-auto object-contain max-h-[200px]" />
              </a>
            ) : (
              <div className="mx-auto" data-testid="tournament-banner">
                <img src={eventData.tournament_banner_url} alt={`${eventData.name || "Tournament"} Banner`} className="w-full h-auto object-contain max-h-[200px]" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Top Ad */}
      {!eventData?.tournament_banner_url && process.env.NODE_ENV !== "development" && (
        <div className="bg-zinc-950 py-2 empty:hidden [&:has(.ad-container:empty)]:hidden">
          <div className="max-w-6xl mx-auto px-4">
            <AdUnit slot="game-top" format="leaderboard" className="mx-auto max-w-[728px]" />
          </div>
        </div>
      )}

      {/* Back Button */}
      <div className="max-w-6xl mx-auto px-4 py-3">
        <Link to="/schedule" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Link>
      </div>

      {/* Header — GameCast / ScoreBird replaces TeamBannerHeader when active */}
      {hasGameCast ? (
        <div className="max-w-6xl mx-auto px-4 py-2">
          <a
            href="https://statmoose.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1.5 py-1 mb-0.5 hover:opacity-80 transition-opacity"
            data-testid="gamecast-powered-strip"
          >
            <span className="text-zinc-500 text-[11px] tracking-wide" style={{ fontFamily: "'Legacy', sans-serif" }}>
              GameCast Powered By
            </span>
            <img src="/statmoose-logo-white.png" alt="StatMoose" className="h-3.5 w-auto opacity-60" />
            <span className="text-zinc-400 text-[11px] tracking-wide" style={{ fontFamily: "'Legacy', sans-serif" }}>
              StatMoose
            </span>
          </a>
          <GameCastHeader header={gameCastData.header} broadcastOverrides={broadcastOverrides} />
        </div>
      ) : hasScoreBird ? (
        <div className="max-w-6xl mx-auto px-4 py-2">
          <ScoreBirdGameCast
            scorebirdLink={scorebirdLink}
            homeTeam={homeTeamObj}
            awayTeam={awayTeamObj}
          />
        </div>
      ) : (
        <TeamBannerHeader broadcast={broadcast} isLive={isLive} isCompleted={isCompleted} isNotStarted={isNotStarted} liveScore={liveScore} />
      )}

      {/* ════════════════════════════════════
         Main Content — state-based layouts
         ════════════════════════════════════ */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <AnimatePresence mode="wait">
          {/* ──── NOT STARTED ──── */}
          {isNotStarted && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {hasGameCast ? (
                <NotStartedGameCastLayout broadcast={broadcast} statMooseCode={statMooseCode} broadcastOverrides={broadcastOverrides} liveScore={liveScore} />
              ) : (
                <NotStartedDefaultLayout broadcast={broadcast} liveScore={liveScore} hasMaxPreps={hasMaxPreps} />
              )}
            </motion.div>
          )}

          {/* ──── LIVE ──── */}
          {isLive && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {hasGameCast ? (
                <LiveGameCastLayout broadcast={broadcast} statMooseCode={statMooseCode} broadcastOverrides={broadcastOverrides} liveScore={liveScore} />
              ) : broadcast.stats_link ? (
                <LiveStatsLayout broadcast={broadcast} liveScore={liveScore} boxScoreData={boxScoreData} />
              ) : (
                <LiveNoStatsLayout broadcast={broadcast} liveScore={liveScore} hasMaxPreps={hasMaxPreps} />
              )}
            </motion.div>
          )}

          {/* ──── COMPLETED ──── */}
          {isCompleted && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {hasGameCast ? (
                <CompletedGameCastLayout broadcast={broadcast} statMooseCode={statMooseCode} broadcastOverrides={broadcastOverrides} />
              ) : broadcast.stats_link ? (
                <CompletedStatsLayout broadcast={broadcast} boxScoreData={boxScoreData} />
              ) : (
                <CompletedNoStatsLayout broadcast={broadcast} hasMaxPreps={hasMaxPreps} />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Bottom Ad */}
      {process.env.NODE_ENV !== "development" && (
        <div className="bg-zinc-950 py-4 empty:hidden [&:has(.ad-container:empty)]:hidden">
          <div className="max-w-6xl mx-auto px-4">
            <AdUnit slot="game-bottom" format="leaderboard" className="mx-auto max-w-[728px]" />
          </div>
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────
// LAYOUT COMPONENTS — one per state x data combo
// ──────────────────────────────────────────

const NotStartedGameCastLayout = ({ broadcast, statMooseCode, broadcastOverrides, liveScore }) => (
  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
    <div className="lg:col-span-8 space-y-4">
      <VideoWithShare broadcast={broadcast} isLive={false} liveScore={null} />
      <StatMooseGameCast code={statMooseCode} hideHeader broadcastOverrides={broadcastOverrides} />
    </div>
    <div className="lg:col-span-4 space-y-4 min-w-0 overflow-hidden">
      <GameCountdown broadcast={broadcast} />
      <GameChat broadcastId={broadcast.id} />
      <Next3GamesWidget currentBroadcastId={broadcast.id} />
    </div>
  </div>
);

const NotStartedDefaultLayout = ({ broadcast, liveScore, hasMaxPreps }) => (
  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
    <div className="lg:col-span-3 space-y-4 min-w-0 overflow-hidden">
      <GameCountdown broadcast={broadcast} />
      <GameInfoPanel broadcast={broadcast} isNotStarted={true} />
    </div>
    <div className="lg:col-span-6 space-y-4">
      <VideoWithShare broadcast={broadcast} isLive={false} liveScore={null} />
      {hasMaxPreps && <MaxPrepsTeamInfo broadcast={broadcast} />}
    </div>
    <div className="lg:col-span-3 space-y-4">
      <GameChat broadcastId={broadcast.id} />
      <Next3GamesWidget currentBroadcastId={broadcast.id} />
    </div>
  </div>
);

const LiveGameCastLayout = ({ broadcast, statMooseCode, broadcastOverrides, liveScore }) => (
  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
    <div className="lg:col-span-8 space-y-4">
      <VideoPlayer broadcast={broadcast} isLive={true} liveScore={liveScore} isExpanded={false} onToggleExpand={() => {}} />
      <StatMooseGameCast code={statMooseCode} hideHeader broadcastOverrides={broadcastOverrides} />
    </div>
    <div className="lg:col-span-4 space-y-4 min-w-0 overflow-hidden">
      <GameChat broadcastId={broadcast.id} />
      <FanPollWidget broadcastId={broadcast.id} />
      <Next3GamesWidget currentBroadcastId={broadcast.id} />
    </div>
  </div>
);

const LiveStatsLayout = ({ broadcast, liveScore, boxScoreData }) => (
  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
    <div className="lg:col-span-3 space-y-4 min-w-0 overflow-hidden">
      <div className="bg-zinc-900/50 border border-white/10 p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
          <span className="text-red-500 text-xs font-bold uppercase">Live Stats</span>
        </div>
        {broadcast.sport?.toLowerCase() === "football" ? (
          <FootballStatsPanel statsUrl={broadcast.stats_link} isLive={true} homeTeam={broadcast.home_team} awayTeam={broadcast.away_team} />
        ) : (
          <LiveStatsPanel statsUrl={broadcast.stats_link} isLive={true} homeTeam={broadcast.home_team} awayTeam={broadcast.away_team} sport={broadcast.sport} />
        )}
      </div>
      <StatLeaders broadcast={broadcast} boxScoreData={boxScoreData} />
      <Next3GamesWidget currentBroadcastId={broadcast.id} />
    </div>
    <div className="lg:col-span-6 space-y-4">
      <VideoPlayer broadcast={broadcast} isLive={true} liveScore={liveScore} isExpanded={false} onToggleExpand={() => {}} />
      <PlayByPlayWidget statsUrl={broadcast.stats_link} sport={broadcast.sport?.toLowerCase() || "basketball"} isLive={true} homeTeam={broadcast.home_team} awayTeam={broadcast.away_team} maxPlays={10} />
      <div className="flex items-center justify-end gap-3">
        <ShareButtons title={broadcast.title} url={window.location.href} />
      </div>
    </div>
    <div className="lg:col-span-3 space-y-4 min-w-0 overflow-hidden">
      <GameChat broadcastId={broadcast.id} />
      <FanPollWidget broadcastId={broadcast.id} />
    </div>
  </div>
);

const LiveNoStatsLayout = ({ broadcast, liveScore, hasMaxPreps }) => (
  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
    <div className="lg:col-span-3 space-y-4 min-w-0 overflow-hidden">
      <FanPollWidget broadcastId={broadcast.id} />
      <GameInfoPanel broadcast={broadcast} isNotStarted={false} />
    </div>
    <div className="lg:col-span-6 space-y-4">
      <VideoWithShare broadcast={broadcast} isLive={true} liveScore={liveScore} />
      {hasMaxPreps && <MaxPrepsTeamInfo broadcast={broadcast} />}
    </div>
    <div className="lg:col-span-3 space-y-4">
      <GameChat broadcastId={broadcast.id} />
      <Next3GamesWidget currentBroadcastId={broadcast.id} />
    </div>
  </div>
);

const CompletedGameCastLayout = ({ broadcast, statMooseCode, broadcastOverrides }) => (
  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
    <div className="lg:col-span-8 space-y-4">
      <VideoWithShare broadcast={broadcast} isLive={false} liveScore={null} />
      <StatMooseGameCast code={statMooseCode} hideHeader broadcastOverrides={broadcastOverrides} />
    </div>
    <div className="lg:col-span-4 space-y-4 min-w-0 overflow-hidden">
      <GameChat broadcastId={broadcast.id} />
      <Next3GamesWidget currentBroadcastId={broadcast.id} />
    </div>
  </div>
);

const CompletedStatsLayout = ({ broadcast, boxScoreData }) => (
  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
    <div className="lg:col-span-3 space-y-4 min-w-0 overflow-hidden">
      <StatLeaders broadcast={broadcast} boxScoreData={boxScoreData} />
      <GameInfoPanel broadcast={broadcast} isNotStarted={false} />
    </div>
    <div className="lg:col-span-6 space-y-4">
      <VideoPlayer broadcast={broadcast} isLive={false} liveScore={null} isExpanded={false} onToggleExpand={() => {}} />
      <TabbedContent broadcast={broadcast} boxScoreData={boxScoreData} />
      <div className="flex items-center justify-end gap-3">
        <ShareButtons title={broadcast.title} url={window.location.href} />
      </div>
    </div>
    <div className="lg:col-span-3 space-y-4 min-w-0 overflow-hidden">
      <GameChat broadcastId={broadcast.id} />
      <Next3GamesWidget currentBroadcastId={broadcast.id} />
    </div>
  </div>
);

const CompletedNoStatsLayout = ({ broadcast, hasMaxPreps }) => (
  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
    <div className="lg:col-span-3 space-y-4 min-w-0 overflow-hidden">
      <GameInfoPanel broadcast={broadcast} isNotStarted={false} />
    </div>
    <div className="lg:col-span-6 space-y-4">
      <VideoWithShare broadcast={broadcast} isLive={false} liveScore={null} />
      {hasMaxPreps && <MaxPrepsTeamInfo broadcast={broadcast} />}
    </div>
    <div className="lg:col-span-3 space-y-4">
      <GameChat broadcastId={broadcast.id} />
      <Next3GamesWidget currentBroadcastId={broadcast.id} />
    </div>
  </div>
);

export default UnifiedGamePage;
